import app from 'firebase/compat/app';

// The user's self-selected preference for horror.
export enum HorrorPreference {
  UNKNOWN,
  HATE,
  DISLIKE,
  NEUTRAL,
  LIKE,
  LOVE
}

export const allHorrorPreferences =
  (<(keyof typeof HorrorPreference)[]>Object.keys(HorrorPreference))
    .filter(key => isNaN(Number(key)) && HorrorPreference[key] > HorrorPreference.UNKNOWN)
    .map(key => HorrorPreference[key]);

export interface TerpecaUser {
  createTime: app.firestore.Timestamp;

  // This can only be changed manually in the database.
  isOwner: boolean;

  // These come from Firebase Auth.
  uid: string;
  email: string;
  displayName?: string;

  // These are provided by the user, and are visible to the public.
  appEmail?: string;
  realName?: string;
  irlRoomCount?: number;      // In-person escape rooms played
  roomCount?: number;         // obsolete: Total rooms played (kept for historical data)
  virtualRoomCount?: number;  // obsolete: Online rooms played (kept for historical data)
  city?: string;
  state?: string;
  country?: string;
  bio?: string;

  // These are provided by the user and are NOT visible to the public.
  slackUsername?: string;
  facebookUsername?: string;
  instagramUsername?: string;
  discordUsername?: string;
  linkedinUsername?: string;
  bestContactMethod?: string;

  mortyAppUsername?: string;
  escapeTheReviewUserId?: string;
  escapeAllGrUserId?: string;
  escapeGameFrUserId?: string;
  escapeRoomersUserId?: string;
  escapeTalkNlUserId?: string;
  lockMeUserId?: string;
  theEscapersUserId?: string;

  // These are provided during the application/upgrade process, but are not otherwise visible.
  roomCountEvidence?: string;
  regionsPlayed?: string;
  onlinePresence?: string;
  otherExperience?: string;
  favoriteRooms?: string;
  affiliations?: string;
  prefsList?: string[];
  references?: string;

  // These are updated as things change.
  status: ApplicationStatus;
  hasResultsAccess?: boolean;
  // Award years in which the nominate-without-voting suspension is waived.
  // This can only be changed manually in the database.
  nominationPenaltyWaived?: number[];
  disabled?: boolean;
  shadowbanned?: boolean;
  upgradeRequested?: boolean;
  vouchList?: string[];
  upgradeVouchList?: string[];
  videoContributionInterest?: boolean;
  horrorPreference?: HorrorPreference;

  applicationDenied?: number[];
  nominationRulesConfirmed?: number[];
  nominationsSubmitted?: number[];
  rankingRulesConfirmed?: number[];
  rankingsSubmitted?: number[];
  auditLogEntry?: UserAuditLogEntry;
  auditLogHistory?: UserAuditLogEntry[];

  // Unique IP addresses, pulled from audit log.
  ipAddresses?: string[];

  // Rooms with a disclosed affiliation.
  affiliatedRoomIds?: string[];

  // Countries served by ambassador
  ambassadorCountries?: string[];
  notifyOnNewApplicant?: boolean;
  notifyOnNewRoom?: boolean;

  // Allows dynamic property access.
  [key: string]: any;
}

export interface TerpecaUserDisclosure {
  // updated from TerpecaUser
  userId: string;  // from uid
  year: number;
  status: ApplicationStatus;
  realName?: string;
  irlRoomCount?: number;      // In-person escape rooms played
  roomCount?: number;         // obsolete: Total rooms played (kept for historical data)
  virtualRoomCount?: number;  // obsolete: Online rooms played (kept for historical data)
  city?: string;
  state?: string;
  country?: string;
  bio?: string;
  isContributor?: boolean;
  affiliatedRoomIds?: string[];
  rankingSubmitted?: boolean;

  // updated from TerpecaRanking
  // as of 2024, this includes played but unranked rooms
  rankedRoomIds?: string[];
  rankedOnlineRoomIds?: string[];

  // Allows dynamic property access.
  [key: string]: any;
}

export function getIpAddressesFromAuditLog(user: TerpecaUser) {
  const ips: string[] = [];
  if (user.auditLogEntry && user.auditLogEntry.uid === user.uid) {
    if (user.auditLogEntry.ipAddress) {
      ips.push(user.auditLogEntry.ipAddress);
    }
    if (user.auditLogEntry.ip6Address && !ips.includes(user.auditLogEntry.ip6Address)) {
      ips.push(user.auditLogEntry.ip6Address);
    }
  }
  for (const auditLogEntry of user.auditLogHistory?.slice().reverse() || []) {
    if (auditLogEntry.uid === user.uid) {
      if (auditLogEntry.ipAddress && !ips.includes(auditLogEntry.ipAddress)) {
        ips.push(auditLogEntry.ipAddress);
      }
      if (auditLogEntry.ip6Address && !ips.includes(auditLogEntry.ip6Address)) {
        ips.push(auditLogEntry.ip6Address);
      }
    }
  }
  return ips;
}

export enum ApplicationStatus {
  NONE = 0,
  PENDING,
  DENIED,
  VOTER,
  NOMINATOR,
  REVIEWER,
  APPROVER
}

export const MIN_ROOMS_FOR_VOTER = 100;
export const MIN_ROOMS_FOR_NOMINATOR = 200;

// First awards year in which nominating without voting incurs a penalty.
export const FIRST_NOMINATION_VOTE_REQUIREMENT_YEAR = 2025;

function maxYearBefore(years: number[] | undefined, asOfYear: number, minYear?: number): number | null {
  let max: number | null = null;
  for (const y of years || []) {
    if (y >= asOfYear) {
      continue;
    }
    if (minYear !== undefined && y < minYear) {
      continue;
    }
    if (max === null || y > max) {
      max = y;
    }
  }
  return max;
}

/**
 * True if the user's most recent nomination (from FIRST_NOMINATION_VOTE_REQUIREMENT_YEAR
 * onward, before asOfYear) is after their most recent vote — i.e. they nominated
 * without voting in that later year, and have not voted since. Never true for a year
 * listed in user.nominationPenaltyWaived.
 */
export function isTemporarilyIneligibleToNominate(user: TerpecaUser, asOfYear: number): boolean {
  if (user.nominationPenaltyWaived?.includes(asOfYear)) {
    return false;
  }
  const lastNominationYear = maxYearBefore(
      user.nominationsSubmitted, asOfYear, FIRST_NOMINATION_VOTE_REQUIREMENT_YEAR);
  if (lastNominationYear === null) {
    return false;
  }
  const lastVoteYear = maxYearBefore(user.rankingsSubmitted, asOfYear + 1);
  return lastVoteYear === null || lastVoteYear < lastNominationYear;
}

/**
 * Most recent nomination year (from FIRST_NOMINATION_VOTE_REQUIREMENT_YEAR onward,
 * before asOfYear) that has not been followed by a subsequent vote, or null if none.
 */
export function getUnresolvedNominationYear(user: TerpecaUser, asOfYear: number): number | null {
  if (!isTemporarilyIneligibleToNominate(user, asOfYear)) {
    return null;
  }
  return maxYearBefore(
      user.nominationsSubmitted, asOfYear, FIRST_NOMINATION_VOTE_REQUIREMENT_YEAR);
}

/**
 * Whether the user may actively nominate for asOfYear: must be at least nominator
 * status and not temporarily suspended for nominating without voting.
 * Owners are not exempted here; callers that want an owner bypass should add it.
 */
export function isEligibleToNominate(user: TerpecaUser, asOfYear: number): boolean {
  if ((user.status || 0) < ApplicationStatus.NOMINATOR) {
    return false;
  }
  return !isTemporarilyIneligibleToNominate(user, asOfYear);
}

export function getStatusString(status: ApplicationStatus): string {
  switch (status) {
    case ApplicationStatus.NONE:
      return 'unsubmitted';
    case ApplicationStatus.PENDING:
      return 'pending';
    case ApplicationStatus.DENIED:
      return 'denied';
    case ApplicationStatus.VOTER:
      return 'voter';
    case ApplicationStatus.NOMINATOR:
      return 'nominator';
    case ApplicationStatus.REVIEWER:
      return 'reviewer';
    case ApplicationStatus.APPROVER:
      return 'approver';
  }
}

export enum ApplicationResubmitReason {
  ONE_PERSON_ONLY,
  INVALID_NAME,
  INVALID_CITY,
  INVALID_FACEBOOK,  // legacy; new requests use INVALID_IDS
  INSUFFICIENT_BIO,
  INSUFFICIENT_EXPERIENCE_INFO,
  CUSTOM,
  INSUFFICIENT_IDENTITY_INFO,
  INSUFFICIENT_CONFLICT_DISCLOSURE,
  INSUFFICIENT_GAME_COUNT,
  INVALID_IDS,
  AFFILIATIONS_NOT_IN_BIO
}

export type AccountIdField =
  'facebookUsername' |
  'instagramUsername' |
  'discordUsername' |
  'linkedinUsername' |
  'mortyAppUsername' |
  'escapeTheReviewUserId' |
  'escapeAllGrUserId' |
  'escapeGameFrUserId' |
  'escapeRoomersUserId' |
  'escapeTalkNlUserId' |
  'lockMeUserId' |
  'theEscapersUserId';

export interface AccountIdFieldInfo {
  field: AccountIdField;
  label: string;
  /** How to find the ID from a profile URL, or a username instruction when there is no URL. */
  idHint: string;
  tracker?: boolean;
  /** Short id used by the partner API and tracking dumps (e.g. "morty"). */
  partnerId?: string;
  /** The user's public profile on the partner site, for the given account id. */
  profileUrl?: (id: string) => string;
}

export const ACCOUNT_ID_FIELDS: AccountIdFieldInfo[] = [
  { field: 'facebookUsername', label: 'Facebook', idHint: 'if the URL is facebook.com/xxxxxx, enter xxxxxx' },
  { field: 'instagramUsername', label: 'Instagram', idHint: 'if the URL is instagram.com/xxxxxx, enter xxxxxx' },
  { field: 'discordUsername', label: 'Discord', idHint: 'enter the username shown on your Discord profile' },
  { field: 'linkedinUsername', label: 'LinkedIn', idHint: 'if the URL is linkedin.com/in/xxxxxx, enter xxxxxx' },
  {
    field: 'mortyAppUsername', label: 'Morty',
    idHint: 'if the URL is morty.app/@xxxxxx, enter xxxxxx', tracker: true, partnerId: 'morty',
    profileUrl: id => `https://morty.app/@${id}/experiences/escape-room/irl`
  },
  {
    field: 'escapeTheReviewUserId', label: 'EscapeTheReview',
    idHint: 'if the URL is escapethereview.co.uk/profile/?user_id=xxxxxx, enter xxxxxx',
    tracker: true, partnerId: 'escapethereview',
    profileUrl: id => `https://www.escapethereview.co.uk/profile/?user_id=${id}`
  },
  {
    field: 'escapeAllGrUserId', label: 'EscapeAll.gr',
    idHint: 'if the URL is escapeall.gr/el/User/Profile/xxxxxx, enter xxxxxx',
    tracker: true, partnerId: 'escapeall',
    profileUrl: id => `https://www.escapeall.gr/en/User/Profile/${id}`
  },
  {
    field: 'escapeGameFrUserId', label: 'EscapeGame.fr',
    idHint: 'if the URL is escapegame.fr/profil/xxxxxx, enter xxxxxx',
    tracker: true, partnerId: 'escapegamefr',
    profileUrl: id => `https://www.escapegame.fr/profil/${id}`
  },
  {
    field: 'escapeRoomersUserId', label: 'Escape-Roomers',
    idHint: 'if the URL is escape-roomers.com/utente_home/xxxxxx, enter xxxxxx',
    tracker: true, partnerId: 'escaperoomers',
    profileUrl: id => `https://escape-roomers.com/utente_home/${id}`
  },
  {
    field: 'escapeTalkNlUserId', label: 'EscapeTalk.nl',
    idHint: 'if the URL is escapetalk.nl/profiel/xxxxxx, enter xxxxxx',
    tracker: true, partnerId: 'escapetalk',
    profileUrl: id => `https://escapetalk.nl/profiel/${id}`
  },
  {
    field: 'lockMeUserId', label: 'Lockme',
    idHint: 'if the URL is lock.me/pl/user/xxxxxx, enter xxxxxx',
    tracker: true, partnerId: 'lockme',
    profileUrl: id => `https://lock.me/pl/user/${id}`
  },
  {
    field: 'theEscapersUserId', label: 'The Escapers',
    idHint: 'if the URL is the-escapers.com/profil/xxxxxx, enter xxxxxx',
    tracker: true, partnerId: 'theescapers',
    profileUrl: id => `https://www.the-escapers.com/profil/${id}`
  }
];

const ACCOUNT_ID_FIELD_BY_KEY = new Map(ACCOUNT_ID_FIELDS.map(info => [info.field, info]));

export function getAccountIdFieldInfo(field: string): AccountIdFieldInfo | undefined {
  return ACCOUNT_ID_FIELD_BY_KEY.get(<AccountIdField>field);
}

export function getAccountIdFieldLabel(field: string): string {
  return getAccountIdFieldInfo(field)?.label || field;
}

export function getTrackerSiteLabels(): string[] {
  return ACCOUNT_ID_FIELDS.filter(info => info.tracker).map(info => info.label);
}

export function getTrackerPartner(partnerId: string): AccountIdFieldInfo | undefined {
  return ACCOUNT_ID_FIELDS.find(info => info.partnerId === partnerId);
}

export function getTrackerPartners(): AccountIdFieldInfo[] {
  return ACCOUNT_ID_FIELDS.filter(info => info.partnerId);
}

export interface ApplicationResubmitRequestPayload {
  status: ApplicationStatus;
  reasons: ApplicationResubmitReason[];
  customInstructions?: string;
  ccEmail?: string;
  invalidIdFields?: string[];
}

export function getResubmitReasonString(
    reason: ApplicationResubmitReason, payload?: ApplicationResubmitRequestPayload): string {
  switch (reason) {
    case ApplicationResubmitReason.INSUFFICIENT_IDENTITY_INFO:
      return 'identity';
    case ApplicationResubmitReason.INSUFFICIENT_GAME_COUNT:
      return 'count';
    case ApplicationResubmitReason.INSUFFICIENT_EXPERIENCE_INFO:
      return 'evidence';
    case ApplicationResubmitReason.INSUFFICIENT_CONFLICT_DISCLOSURE:
      return 'disclosure';
    case ApplicationResubmitReason.AFFILIATIONS_NOT_IN_BIO:
      return 'affiliations';
    case ApplicationResubmitReason.ONE_PERSON_ONLY:
      return 'group';
    case ApplicationResubmitReason.INVALID_NAME:
      return 'name';
    case ApplicationResubmitReason.INVALID_CITY:
      return 'city';
    case ApplicationResubmitReason.INVALID_FACEBOOK:
      return 'facebook';
    case ApplicationResubmitReason.INVALID_IDS: {
      const labels = (payload?.invalidIdFields || []).map(field => getAccountIdFieldLabel(field));
      return labels.length ? `ids (${labels.join(', ')})` : 'ids';
    }
    case ApplicationResubmitReason.INSUFFICIENT_BIO:
      return 'bio';
    case ApplicationResubmitReason.CUSTOM:
      return 'custom';
  }
}

export enum ApplicationDenyReason {
  DUPLICATE_ACCOUNT,
  NOT_ENOUGH_ROOMS,
  CUSTOM
}

export interface ApplicationDenyRequestPayload {
  status: ApplicationStatus;
  reasons: ApplicationDenyReason[];
  duplicateAccountEmail?: string;
  customInstructions?: string;
  ccEmail?: string;
}

export function getDenyReasonString(reason: ApplicationDenyReason): string {
  switch (reason) {
    case ApplicationDenyReason.DUPLICATE_ACCOUNT:
      return 'duplicate';
    case ApplicationDenyReason.NOT_ENOUGH_ROOMS:
      return 'rooms';
    case ApplicationDenyReason.CUSTOM:
      return 'custom';
  }
}

export interface UserAuditLogEntry {
  uid: string;
  name: string;
  entryType: UserAuditLogEntryType;
  payload: any;
  timestamp: app.firestore.Timestamp;
  ipAddress?: string;
  ip6Address?: string;
}

export const REVIEWER_EDITABLE_FIELDS = <const>[
  'realName',
  'city',
  'state',
  'country',
  'irlRoomCount',
  'bio',
  'facebookUsername',
  'instagramUsername',
  'discordUsername',
  'linkedinUsername',
  'mortyAppUsername',
  'escapeTheReviewUserId',
  'escapeAllGrUserId',
  'escapeGameFrUserId',
  'escapeRoomersUserId',
  'escapeTalkNlUserId',
  'lockMeUserId',
  'theEscapersUserId'
];

export type ReviewerEditableField = typeof REVIEWER_EDITABLE_FIELDS[number];

export const REVIEWER_EDITABLE_FIELD_LABELS: { [key in ReviewerEditableField]: string } = {
  realName: 'Name',
  city: 'City',
  state: 'State',
  country: 'Country',
  irlRoomCount: 'In-Person Rooms',
  bio: 'Bio',
  facebookUsername: 'Facebook',
  instagramUsername: 'Instagram',
  discordUsername: 'Discord',
  linkedinUsername: 'LinkedIn',
  mortyAppUsername: 'Morty',
  escapeTheReviewUserId: 'EscapeTheReview',
  escapeAllGrUserId: 'EscapeAll.gr',
  escapeGameFrUserId: 'EscapeGame.fr',
  escapeRoomersUserId: 'Escape-Roomers',
  escapeTalkNlUserId: 'EscapeTalk.nl',
  lockMeUserId: 'Lockme',
  theEscapersUserId: 'The Escapers'
};

export interface ApplicantFieldChange {
  before: string;
  after: string;
}

export interface ApplicantFieldsEditedPayload {
  [field: string]: ApplicantFieldChange;
}

export enum UserAuditLogEntryType {
  APPLICATION_STATUS_CHANGED,
  NOMINATIONS_SUBMITTED,
  RANKINGS_SUBMITTED,
  VOUCH_ADDED,
  VOUCH_REMOVED,
  RANKINGS_REOPENED,
  UPGRADE_REQUESTED,
  UPGRADE_DENIED,
  NOMINATIONS_REOPENED,
  UPGRADE_VOUCH_ADDED,
  UPGRADE_VOUCH_REMOVED,
  REVIEW_NOTE_ADDED,
  SHADOW_BAN_ON,
  SHADOW_BAN_OFF,
  NOMINATIONS_REVOKED,
  RANKINGS_REVOKED,
  APPLICANT_FIELDS_EDITED,
  ACCOUNT_DISABLED,
  ACCOUNT_ENABLED,
  NOMINATION_PENALTY_WAIVED,
  NOMINATION_PENALTY_WAIVER_REMOVED
}

export interface NominationPenaltyWaiverPayload {
  year: number;
  note?: string;
}

export function getUserAuditLogString(entry: UserAuditLogEntry | undefined) {
  let description = '';
  if (!entry) {
    return description;
  }
  switch (entry.entryType) {
    case UserAuditLogEntryType.APPLICATION_STATUS_CHANGED:
      if (typeof entry.payload === 'number') {
        description = `status changed to ${getStatusString(<ApplicationStatus>entry.payload)}`;
      } else {
        description = `status changed to ${getStatusString(<ApplicationStatus>entry.payload.status)}`;
        if (entry.payload.reasons) {
          if (entry.payload.status === ApplicationStatus.DENIED) {
            description += ' (' + <string>entry.payload.reasons.map(
              (reason: ApplicationDenyReason) => getDenyReasonString(reason)
            ).join(', ') + ')';
          } else {
            description += ' (' + <string>entry.payload.reasons.map(
              (reason: ApplicationResubmitReason) => getResubmitReasonString(reason, entry.payload)
            ).join(', ') + ')';
          }
        }
      }
      break;
    case UserAuditLogEntryType.VOUCH_ADDED:
      description = `vouch added` + (entry.payload ? `: ${entry.payload}` : '');
      break;
    case UserAuditLogEntryType.VOUCH_REMOVED:
      description = `vouch removed` + (entry.payload ? `: ${entry.payload}` : '');
      break;
    case UserAuditLogEntryType.UPGRADE_VOUCH_ADDED:
      description = `upgrade vouch added` + (entry.payload ? `: ${entry.payload}` : '');
      break;
    case UserAuditLogEntryType.UPGRADE_VOUCH_REMOVED:
      description = `upgrade vouch removed` + (entry.payload ? `: ${entry.payload}` : '');
      break;
    case UserAuditLogEntryType.NOMINATIONS_SUBMITTED:
      description = `${entry.payload} nominations submitted`;
      break;
    case UserAuditLogEntryType.NOMINATIONS_REOPENED:
      description = `${entry.payload} nominations reopened`;
      break;
    case UserAuditLogEntryType.RANKINGS_SUBMITTED:
      description = `${entry.payload} rankings submitted`;
      break;
    case UserAuditLogEntryType.RANKINGS_REOPENED:
      description = `${entry.payload} rankings reopened`;
      break;
    case UserAuditLogEntryType.UPGRADE_REQUESTED:
      description = `upgrade requested`;
      break;
    case UserAuditLogEntryType.UPGRADE_DENIED:
      description = `upgrade declined`;
      if (entry.payload?.reasons) {
        description += ' (' + <string>entry.payload.reasons.map(
          (reason: ApplicationResubmitReason) => getResubmitReasonString(reason, entry.payload)
        ).join(', ') + ')';
      }
      break;
    case UserAuditLogEntryType.REVIEW_NOTE_ADDED:
      description = `review note: ${entry.payload}`;
      break;
    case UserAuditLogEntryType.SHADOW_BAN_ON:
      description = `shadow ban on` + (entry.payload ? `: ${entry.payload}` : '');
      break;
    case UserAuditLogEntryType.SHADOW_BAN_OFF:
      description = `shadow ban off` + (entry.payload ? `: ${entry.payload}` : '');
      break;
    case UserAuditLogEntryType.NOMINATIONS_REVOKED:
      description = `${entry.payload} nominations revoked`;
      break;
    case UserAuditLogEntryType.RANKINGS_REVOKED:
      description = `${entry.payload} rankings revoked`;
      break;
    case UserAuditLogEntryType.APPLICANT_FIELDS_EDITED:
      description = 'fields edited: ' + Object.keys(entry.payload || {}).map((field) => {
        const label = REVIEWER_EDITABLE_FIELD_LABELS[<ReviewerEditableField>field] || field;
        const change = <ApplicantFieldChange>entry.payload[field];
        return `${label} "${formatAuditFieldValue(change?.before)}" → "${formatAuditFieldValue(change?.after)}"`;
      }).join('; ');
      break;
    case UserAuditLogEntryType.ACCOUNT_DISABLED:
      description = `account disabled` + (entry.payload ? `: ${entry.payload}` : '');
      break;
    case UserAuditLogEntryType.ACCOUNT_ENABLED:
      description = `account enabled` + (entry.payload ? `: ${entry.payload}` : '');
      break;
    case UserAuditLogEntryType.NOMINATION_PENALTY_WAIVED:
    case UserAuditLogEntryType.NOMINATION_PENALTY_WAIVER_REMOVED: {
      const payload = <NominationPenaltyWaiverPayload>entry.payload;
      const action = entry.entryType === UserAuditLogEntryType.NOMINATION_PENALTY_WAIVED ? 'waived' : 'waiver removed';
      description = `nomination suspension ${action} for ${payload?.year}` + (payload?.note ? `: ${payload.note}` : '');
      break;
    }
  }
  return `${description} - ${entry.name} ${entry.timestamp.toDate().toLocaleString()}${entry.ipAddress ? ' ' + entry.ipAddress : ''}${entry.ip6Address ? ' ' + entry.ip6Address : ''}`;
}

function formatAuditFieldValue(value: string | undefined): string {
  if (!value) {
    return '(empty)';
  }
  if (value.length > 80) {
    return value.substring(0, 80) + '...';
  }
  return value;
}
